import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Box,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { formatDate, sequence } from '../../../common/utils';
import SatelliteImageThumbnail from '../visualization/SatelliteImageThumbnail';

/**
 * @function CompositeImageTable
 * @description Paginated, sortable table of satellite images with thumbnail previewand per-row / select-all checkboxes.
 * Thumbnails are rendered only for rows currently visible on screen.
 * <SatelliteImageThumbnail> owns its own module-level cache and staggered timeout, so no N simultaneous GEE requests fire
 * on mount and results arereused when the user pages back.
 * @param {Object[]} images - Array of image objects { id, date, shortname, name, content }
 * @param {Object} selectedImages - Map of { [id]: boolean } for checkbox state
 * @param {Function} onCheckboxChange - (key, checked) => void
 * @param {Function} onSelectAll - (keys, checked) => void
 * @param {Function} t - i18n translation function
 * @param {Object} roi - GEE geometry object (roiRef.current)
 * @param {Object} collectionRef - React ref holding the ee.ImageCollection
 * **Called by:
 * @see {PeriodChooser} -Bathymetry module
 * @memberof module:Bathymetry
 * @returns {JSX.Element}
 */
const CompositeImageTable = ({
  images = [],
  selectedImages = {},
  onCheckboxChange,
  onSelectAll,
  t,
  roi,
  collectionRef,
}) => {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(20);
  const [orderIndex, setOrderIndex] = useState(null);
  const [order, setOrder] = useState('asc');
  const [sorted, setSorted] = useState(() => sequence(images.length, 0));

  /**
   * @function getKey
   * @description Returns a unique key for an image based on its identifier.
   * @param {Object} img - Image object.
   * **Called by:
   * @see {handleCheckAll}
   * @see {CompositeImageTable} - directly in the row/checkbox render.
   * @memberof module:Bathymetry
   * @returns {string} String representation of the image identifier.
   */
  const getKey = useCallback((img) => `${img.id}`, []);

  useEffect(() => {
    setSorted(sequence(images.length, 0));
    setPage(0);
  }, [images.length]);

  const start = page * rows;
  const visibleRows = sorted.slice(start, start + rows);
  const visibleSet = useMemo(() => new Set(visibleRows), [visibleRows]);

  const COLUMNS = useMemo(() => {
    return [
      {
        id: 'id',
        label: t('forms.acquisition.4.table.id') || 'ID',
        selector: (i) => {
          const img = images[i];
          const sn = img?.name ?? img?.id ?? '—';
          const dt = img?.date ? formatDate(img.date, true) : '—';
          return `${sn} — ${dt}`;
        },
        getValue: (i) => images[i]?.date ?? '',
      },
      {
        id: 'preview',
        label: t('forms.acquisition.4.table.thumbnail') || 'Preview',
        selector: (i) => {
          if (!roi || !collectionRef || !visibleSet.has(i)) return null;
          return (
            <SatelliteImageThumbnail
              id={images[i]?.id}
              roi={roi}
              height={80}
              collectionRef={collectionRef}
              bands={images[i].bands}
            />
          );
        },
        getValue: () => 0,
      },
    ];
  }, [images, t, roi, collectionRef, visibleSet]);

  const selectedCount = useMemo(
    () => images.reduce((acc, img) => acc + (selectedImages[getKey(img)] ? 1 : 0), 0),
    [images, selectedImages, getKey],
  );

  const allChecked = useMemo(() => {
    if (!images.length) return 0;
    if (selectedCount === 0) return 0;
    if (selectedCount === images.length) return 1;
    return -1;
  }, [images.length, selectedCount]);

  /**
   * @function handleSort
   * @description Sorts the image list using the selected column. Updates the sorted indices, current sort order, and active sort column.
   * @param {number} ci - Index of the column used for sorting.
   * **Called by:
   * @see {CompositeImageTable} - via each column header's TableSortLabel onClick.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleSort = useCallback(
    (ci) => {
      const col = COLUMNS[ci];
      const newOrder = ci === orderIndex && order === 'desc' ? 'asc' : 'desc';
      const next = sequence(images.length, 0);
      next.sort((a, b) => {
        const va = col.getValue(a),
          vb = col.getValue(b);
        const v = va < vb ? 1 : va > vb ? -1 : 0;
        return newOrder === 'desc' ? v : -v;
      });
      setSorted(next);
      setOrder(newOrder);
      setOrderIndex(ci);
    },
    [COLUMNS, images.length, order, orderIndex],
  );

  /**
   * @function handleCheckAll
   * @description Selects or deselects all images in the current list. Generates the image keys and forwards them to the parent selection handler.
   * @param {boolean} checked - Indicates whether all images should be selected or deselected
   * @see {@link getKey} - generates the image keys forwarded to onSelectAll.
   * **Called by:
   * @see {CompositeImageTable} - via the header row's "select all" Checkbox onChange.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleCheckAll = useCallback(
    (checked) => onSelectAll(images.map(getKey), checked),
    [images, getKey, onSelectAll],
  );

  return (
    <>
      {images.length === 0 ? (
        <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 12, padding: 32 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ color: '#555' }}>
            {t('forms.acquisition.3.bathymetry.loadingImages') || 'Fetching image list from GEE…'}
          </Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col, i) => (
                <TableCell key={col.id} sortDirection={orderIndex === i ? order : false}>
                  <TableSortLabel
                    active={orderIndex === i}
                    direction={orderIndex === i ? order : 'asc'}
                    onClick={() => handleSort(i)}>
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>
                {t('forms.acquisition.4.table.selected') || 'Use'}
                <Checkbox
                  checked={allChecked === 1}
                  indeterminate={allChecked === -1}
                  onChange={(e) => handleCheckAll(e.target.checked)}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((idx) => {
              const img = images[idx];
              const key = getKey(img);
              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => onCheckboxChange(key, !selectedImages[key])}
                  style={{ cursor: 'pointer' }}>
                  {COLUMNS.map((col) => (
                    <TableCell
                      align="left"
                      key={col.id}
                      onClick={col.id === 'preview' ? (e) => e.stopPropagation() : undefined}>
                      {col.selector(idx)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Checkbox
                      checked={!!selectedImages[key]}
                      onChange={(e) => {
                        e.stopPropagation();
                        onCheckboxChange(key, e.target.checked);
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={COLUMNS.length + 1}
                count={images.length}
                rowsPerPage={rows}
                page={page}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRows(Number(e.target.value));
                  setPage(0);
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </>
  );
};

export default CompositeImageTable;
